/**
 * Área "Repositório de Arquivos": a pasta fixa `admin-arquivos` do bucket `materiais`, fora de
 * qualquer cliente. Porte do admin atual (`portal/admin.html:1798-1802`, `6344-6438`).
 *
 * O QUE A PESSOA FAZ AQUI, igual ao legado: vê os arquivos da pasta, cada um com "Abrir" (link
 * assinado de uma hora, em nova aba) e "Excluir" (com confirmação); escolhe um arquivo e clica em
 * "Enviar arquivo". O nome vai sem acento e sem caractere especial, e enviar de novo um arquivo de
 * mesmo nome o substitui. O arquivo escolhido e a última frase de status FICAM se ela sair para
 * outra área e voltar, como ficam no documento do legado (ver o bloco sobre isso abaixo).
 *
 * O RECORTE POR PAPEL, medido em `admin.html:3375-3397`: o menu só mostra esta área a
 * administrador e consulta. Para quem não é administrador somem o "Excluir" e todo link que ficou
 * apontando para `#` (o "Abrir" cujo link assinado falhou); para consulta, o campo de arquivo fica
 * travado e o "Enviar arquivo" some.
 *
 * O QUE FICA IGUAL MESMO PARECENDO DEFEITO, e vai para a lista do fundador: o legado ignora a
 * falha da listagem (`if (error ...) continue`, `admin.html:6382`) e mostra "Nenhum arquivo ainda."
 * também quando o Storage recusa. Migração reproduz; a correção é decisão dele.
 */

// Estado, referência, efeito e a assinatura de um estado que vive fora do React.
import { useCallback, useEffect, useLayoutEffect, useRef, useState, useSyncExternalStore } from 'react';
// O botão da plataforma: o produto não desenha botão.
import { Botao } from '@complexity/ui';
// O dicionário: a frase em português é a chave.
import { t } from '@complexity/i18n';
// A tradução de erro e a limpeza do nome, com as regras exatas do legado.
import { sanitizarNomeArquivo, traduzirErro, CAMINHO_ARQUIVOS_ADMIN } from '@studiolu/dados';
// A área de dados: as mesmas quatro chamadas de Storage do legado.
import {
  enviarArquivoAdministrativo,
  excluirArquivoDaPasta,
  linkAssinadoDoArquivo,
  listarArquivosAdministrativos,
} from '@studiolu/dados/areas/arquivos';
// Sessão, papel e fonte de dados de quem está no painel.
import { usePainel } from '../../contexto.js';
// A confirmação e o aviso do legado, peça comum; o achado 5 mora dentro dela.
import { useConfirmacao } from '../../comum/confirmacao.js';
// A linha de status do legado, com `role="status"`, peça comum.
import { LinhaDeStatus } from '../../comum/status-temporario.js';

// O limite de envio do legado: 10 MB (`TAMANHO_MAX`, `admin.html:7716`).
const TAMANHO_MAX = 10 * 1024 * 1024;

/* ===========================================================================
   O QUE SOBREVIVE À TROCA DE ÁREA, como no legado (linha 175 do inventário)
   ===========================================================================
   No legado a área é uma aba da mesma página: o clique em outra aba só troca classes
   (`admin.html:6440-6456`), então o `<input type="file">` continua no documento COM o arquivo
   escolhido, e o `#status-upload-arquivos-admin` continua com a última frase. Quem escolhe um
   arquivo, vai olhar outra área e volta, termina o envio; e o "Enviado!" some 3 s depois, esteja a
   aba na tela ou não, porque o relógio é da página (`6435`). Nem o "Sair" apaga isso: ele não
   recarrega a página (`3358`).

   Aqui a tela DESMONTA ao trocar de rota, então as duas coisas moram fora dela, no módulo, que
   avalia uma vez por carga da página. O arquivo volta para o campo pelo `DataTransfer`, que é o
   único jeito de devolver um `File` a um `<input type="file">` sem a pessoa escolher de novo.

   A peça comum `useStatusTemporario` não serve aqui: o texto dela morre com o componente, e o
   relógio dela também. O que muda é só ONDE o texto mora; a regra é a mesma, e é a do legado:
   apaga depois do prazo, e só se a frase ainda for a mesma. */

// O arquivo escolhido e ainda não enviado.
let arquivoDaPagina: File | null = null;
// A última frase do status, que a pessoa lê ao voltar.
let statusDaPagina = '';
// Quem está na tela agora, para redesenhar quando o status muda de fora dela.
const ouvintesDoStatus = new Set<() => void>();

// Escreve o status na página e avisa quem estiver na tela.
function escreverStatus(texto: string): void {
  statusDaPagina = texto;
  for (const avisar of ouvintesDoStatus) avisar();
}

// Escreve e apaga depois do prazo, só se a frase ainda for a mesma (`admin.html:6435`).
function escreverStatusTemporario(texto: string, prazoMs = 3000): void {
  escreverStatus(texto);
  window.setTimeout(() => {
    if (statusDaPagina === texto) escreverStatus('');
  }, prazoMs);
}

// A assinatura que o React usa para ler o status de fora dele.
function assinarStatus(avisar: () => void): () => void {
  ouvintesDoStatus.add(avisar);
  return () => ouvintesDoStatus.delete(avisar);
}

// Um arquivo como a lista o desenha: o nome e o link assinado, ou `null` quando ele falhou.
interface LinhaDeArquivo {
  readonly nome: string;
  readonly link: string | null;
}

export function TelaArquivos() {
  const { dados, acesso } = usePainel();
  // A confirmação de excluir e o aviso de erro.
  const { confirmar, alertar, dialogo } = useConfirmacao();
  // `null` antes da primeira leitura: o legado começa com a lista em branco, sem "Carregando".
  const [linhas, setLinhas] = useState<readonly LinhaDeArquivo[] | null>(null);
  // O texto de andamento do envio (`#status-upload-arquivos-admin`), lido da página: só o
  // "Enviado!" some sozinho em 3 s (`admin.html:6435`); os outros ficam até a próxima mensagem.
  const status = useSyncExternalStore(assinarStatus, () => statusDaPagina, () => statusDaPagina);
  // O campo de arquivo, para ler o escolhido e limpar depois do envio, como o legado.
  const campo = useRef<HTMLInputElement>(null);
  // Só o administrador exclui (`admin.html:3386-3389`).
  const ehAdministrador = acesso.papel === 'administrador';
  // Consulta não envia nada (`admin.html:3393-3395`).
  const somenteLeitura = acesso.papel === 'consulta';

  // Lê a pasta e o link de cada arquivo, como `listarArquivosNaPasta` (`admin.html:6376-6390`).
  const carregar = useCallback(async () => {
    const lista = await listarArquivosAdministrativos(dados);
    // DEFEITO DO LEGADO, REPRODUZIDO POR DECISÃO DO FUNDADOR E ISOLADO NESTE PONTO: falha do Storage
    // e pasta vazia dão a mesma lista vazia ("Nenhum arquivo ainda."), como o `if (error || ...)
    // continue` de `admin.html:6382`. Corrigir é separar o `!lista.ok` daqui, e só aqui.
    if (!lista.ok || lista.dados.length === 0) {
      setLinhas([]);
      return;
    }
    // O link de cada arquivo; falha vira `null`, que o legado desenha como `href="#"`.
    const comLink = await Promise.all(
      lista.dados.map(async (arquivo) => {
        const assinado = await linkAssinadoDoArquivo(dados, CAMINHO_ARQUIVOS_ADMIN, arquivo.name);
        return { nome: arquivo.name, link: assinado.ok ? assinado.dados.signedUrl : null };
      }),
    );
    setLinhas(comLink);
  }, [dados]);

  // Lê ao abrir a área, como o clique na aba do legado (`admin.html:6454`).
  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Devolve ao campo o arquivo que estava escolhido antes da troca de área, para a tela voltar como
  // o legado a deixa. Antes da pintura, para o campo nunca aparecer vazio por um instante.
  useLayoutEffect(() => {
    if (!arquivoDaPagina || !campo.current || campo.current.files?.length) return;
    // O `files` de um `<input>` só aceita um `FileList`, e o `DataTransfer` é o único jeito de
    // montar um na mão. O `File` é o mesmo objeto, então o envio manda o conteúdo de verdade.
    const pacote = new DataTransfer();
    pacote.items.add(arquivoDaPagina);
    campo.current.files = pacote.files;
  });

  // "Excluir": confirma com o nome, apaga, e recarrega (`admin.html:6362-6368`).
  const excluir = useCallback(
    async (nome: string) => {
      if (!(await confirmar(`Excluir o arquivo "${nome}"?`))) return;
      const resultado = await excluirArquivoDaPasta(dados, CAMINHO_ARQUIVOS_ADMIN, nome);
      // A falha em aviso, com a mensagem crua depois do prefixo, como o legado.
      if (!resultado.ok) {
        await alertar(`Erro ao excluir: ${resultado.erro.mensagem}`);
        return;
      }
      await carregar();
    },
    [dados, confirmar, alertar, carregar],
  );

  // "Enviar arquivo", passo a passo como `admin.html:6424-6438`.
  const enviar = useCallback(async () => {
    const arquivo = campo.current?.files?.[0];
    // Sem arquivo escolhido: a frase do legado, e nada vai para o Storage.
    if (!arquivo) {
      escreverStatus('Escolhe um arquivo primeiro.');
      return;
    }
    // Acima de 10 MB: recusa antes de enviar.
    if (arquivo.size > TAMANHO_MAX) {
      escreverStatus('Arquivo muito grande. O limite é 10 MB.');
      return;
    }
    escreverStatus('Enviando...');
    // O nome sem acento e sem caractere especial, que o Storage aceita.
    const resultado = await enviarArquivoAdministrativo(dados, sanitizarNomeArquivo(arquivo.name), arquivo);
    // Aqui o legado TRADUZ a mensagem (`traduzirErro`, `admin.html:6433`), e não a mostra crua.
    if (!resultado.ok) {
      escreverStatus(traduzirErro(resultado.erro.mensagem));
      return;
    }
    // Some sozinho em 3 segundos, se nada mais tiver escrito no lugar dele.
    escreverStatusTemporario('Enviado!');
    // Limpa o campo, na tela e na página, e a lista volta com o arquivo novo.
    if (campo.current) campo.current.value = '';
    arquivoDaPagina = null;
    await carregar();
  }, [dados, carregar]);

  return (
    <div className="flex flex-col gap-cx-3">
      {/* A lista: nada antes da primeira leitura, a frase do vazio, ou uma linha por arquivo. */}
      <div>
        {linhas !== null && linhas.length === 0 && (
          <p className="text-cx-sm text-texto-suave">{t('Nenhum arquivo ainda.')}</p>
        )}
        {linhas?.map((linha) => (
          // A linha do legado: nome à esquerda, ações à direita, quebrando no celular (`.arquivo-linha`).
          <div
            key={linha.nome}
            data-arquivo={linha.nome}
            className="flex flex-wrap items-center justify-between gap-cx-3 border-b border-borda py-cx-3 text-cx-base text-texto [overflow-wrap:anywhere]"
          >
            <span>{linha.nome}</span>
            <span className="flex shrink-0 items-center gap-cx-4">
              {/* "Abrir": o link assinado em nova aba. Sem link, o legado aponta para `#`, e esse
                  link some para quem não é administrador (`a[href="#"]`, `admin.html:3375`). */}
              {(linha.link || ehAdministrador) && (
                <a
                  href={linha.link ?? '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-toque items-center text-texto"
                >
                  {t('Abrir')}
                </a>
              )}
              {/* "Excluir": texto vermelho sublinhado, como `.excluir-link`; só para administrador. */}
              {ehAdministrador && (
                <a
                  href="#"
                  className="inline-flex min-h-toque items-center text-cx-sm text-erro underline underline-offset-4"
                  onClick={(evento) => {
                    // O `#` não navega: é o `preventDefault` do legado.
                    evento.preventDefault();
                    void excluir(linha.nome);
                  }}
                >
                  {t('Excluir')}
                </a>
              )}
            </span>
          </div>
        ))}
      </div>

      {/* O campo de arquivo do legado, com o mesmo rótulo acessível. Não há peça de arquivo
          genérico no Chassi (o `CampoAnexo` é só de imagem e reduz o que recebe); este é o
          `input` nativo que o legado usa, travado para consulta. */}
      <input
        ref={campo}
        type="file"
        aria-label={t('Arquivo administrativo para enviar')}
        disabled={somenteLeitura}
        className="text-cx-sm text-texto"
        // O escolhido fica guardado na página: é o que o campo do legado continua tendo ao voltar
        // de outra área. Desistir da escolha zera os dois, como o campo vazio do legado.
        onChange={(evento) => {
          arquivoDaPagina = evento.target.files?.[0] ?? null;
        }}
      />
      {/* "Enviar arquivo" some para consulta, como todo botão de ação desse papel. */}
      {!somenteLeitura && (
        <div>
          <Botao onClick={() => void enviar()}>{t('Enviar arquivo')}</Botao>
        </div>
      )}
      {/* O andamento do envio, anunciado ao leitor de tela (`role="status"` no legado). */}
      <LinhaDeStatus texto={status} testid="status-upload-arquivos-admin" />
      {dialogo}
    </div>
  );
}
